import uniqueId from 'lodash/uniqueId';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import BasicSection from '../../components/BasicSection';
import CandidateExperience from '../../components/CandidateExperience';
import DataList from '../../components/DataList';
import LinkButton from '../../components/LinkButton';
import PanelContainer from '../../components/PanelContainer';
import ProfileContentLoader from '../../components/ProfileContentLoader';
import ProfileHeader from '../../components/ProfileHeader';
import ReadMore from '../../components/ReadMore';
import ENDPOINT from '../../constants/endpoints';
import ROUTE from '../../constants/route';
import {Permission, Role} from '../../helpers/user';
import useApi from '../../hooks/useApi';
import PageLayout from '../../layouts/PageLayout';
import CandidateProfile from '../../scenes/CandidateProfile';
import ProfileItemEducation from '../../scenes/CandidateProfile/components/ProfileItemEducation';
import ProfileItemReference from '../../scenes/CandidateProfile/components/ProfileItemReference';
import Sidebar from '../../scenes/Sidebar';
import CandidateInterviewDataWidget from '../../scenes/Sidebar/components/CandidateInterviewDataWidget';
import CandidateNotesWidget from '../../scenes/Sidebar/components/CandidateNotesWidget';
import UpdatedProfile from '../../scenes/Sidebar/components/UpdatedProfile';
import action from './actions.js';
import './styles.css';

const CandidateProfileEditor = props => {
  const {
    user,
    history,
    match: {
      params: { id },
    },
  } = props;

  const [isRecruiterPremiumRecFirm] = useMemo(
    () => [
      Role.isRecruiterPremiumRecFirm(user),
    ],
    [user]
  );

  const api = useApi();
  const [state, setState] = useState({
    init: false,
    profile: {},
    panels: {},
    windowWidth: window.innerWidth + 'px',
  });
  const widgetRef = useRef();

  const loadData = () =>
    api.get(
      ENDPOINT.ENTITY.EDIT_CANDIDATE_PROFILE,
      { id },
      {
        handleForbidden: () => history.replace(ROUTE.ERROR_404),
        handleNotFound: () => history.replace(ROUTE.ERROR_404),
        handleSuccess: data => {
          setState({
            init: true,
            profile: {...data},
          });
        }
      }
    );

  useEffect(() => {
    loadData();
  }, []);

  const handleEditPhoto = () =>
    action.editPhoto(state, id, {
      setState,
      callback: widgetRef.current.reload,
    });

  const handleDeletePhoto = () =>
    action.deletePhoto(state, api, id, {
      setState,
      callback: widgetRef.current.reload,
    });

  const handleUploadResume = () =>
    action.uploadResume(state, id, {
      setState,
      callback: widgetRef.current.reload,
    });

  const handleAddPosition = () =>
    action.addPosition(state, user, id, {
      loadData,
      callback: widgetRef.current.reload,
    });

  const handleEditPosition = ({ PositionID }) => {
    action.editPosition(state, user, id, PositionID, {
      loadData,
      callback: widgetRef.current.reload,
    });
  }

  const handleDeletePosition = ({ PositionID }) =>
    action.deletePosition(api, id, PositionID, {
      loadData,
      callback: widgetRef.current.reload,
    });

  const handleAddEducation = () =>
    action.addEducation(state, user, id, {
      loadData,
      callback: widgetRef.current.reload,
    });

  const handleEditEducation = ({ PositionID, UndergraduateSchoolID }) => {
    if (!!UndergraduateSchoolID) {
      action.editBAEducation(state, id, PositionID, {
        loadData,
        callback: widgetRef.current.reload,
      });
    } else {
      action.editEducation(state, user, id, PositionID, {
        loadData,
        callback: widgetRef.current.reload,
      });
    }
  };

  const handleAddReference = () =>
    action.addReference(state, id, {
      setState,
      callback: widgetRef.current.reload,
    });

  const handleEditAcademics = () =>
    action.editConfidentialInfo(state, user, 'Academics', id, {
      setState,
      callback: widgetRef.current.reload,
    });

  const handleEditContacts = () =>
    action.editConfidentialInfo(state, user, 'Contact', id, {
      setState,
      callback: widgetRef.current.reload,
    });

  const handleEditPersonalInfo = () =>
    action.editPersonalInfo(state, id, {
      setState,
      callback: widgetRef.current.reload,
    });

  const handleEditPreferences = () =>
    action.editPreferences(state, id, {
      setState,
      callback: widgetRef.current.reload,
    });

  const handleEditSummary = () =>
    action.editSummary(state, id, {
      setState,
      callback: widgetRef.current.reload,
    });

  const handleInterviewDataUpdate = () =>
    action.editInterviewData(state, user, id, {
      setState,
      callback: widgetRef.current.reload,
    });

  const rendererHeader = useMemo(() => {
    if (state.init) {
      const {
        FullName,
        PictureUrl,
        EmailAddress,
        ResumeUpdateData,
        ResumeUrl,
        Resumes,
        Confidential: { EmailAddress2, PhoneNumber1, PhoneNumber2 },
        Positions: [position],
        experience,
        isRecruiter,
      } = state.profile;

      const resumeHistory = Resumes && !!Resumes.length && (
        <div className="profile-header__old-resumes">
          <p
            className="profile-header__text"
            style={{
              margin: '0',
            }}
          >
            Old Resumes
          </p>

          {Resumes.map(item => (
            <span className="profile-header__old-resume" key={item.id}>
              <LinkButton
                text={item.date}
                btnFontFamily="Noto Sans, sans-serif"
                btnFontWeight="400"
                btnFontSize="0.8571rem"
                controlFunc={() => action.downloadResume(id, item.id)}
              />
            </span>
          ))}
        </div>
      );

      const actions = {
        onPhotoEditHandler: handleEditPhoto,
        onPhotoDeleteHandler: handleDeletePhoto,
        onResumeUploadHandler: handleUploadResume,
        onEditConfidential: handleEditContacts,
        onEditPosition: handleEditPosition,
        onEditEducation: handleEditEducation,
        onDownloadResume: () => action.downloadResume(id),
      };

      if (Permission.isAllowedDeleteCandidatePosition(user.permissions)) {
        actions.onDeletePosition = handleDeletePosition;
      }

      return (
        <ProfileHeader
          isRecruiter={isRecruiter}
          isAddReviewBtnHidden
          isFirmRatingHidden
          resumeHistory={resumeHistory}
          data={{
            PictureUrl,
            Resumes,
            FullName,
            EmailAddress,
            ResumeUpdateData,
            ResumeUrl,
            EmailAddress2,
            PhoneNumber1,
            PhoneNumber2,
            position,
            experience,
          }}
          actions={actions}
        />
      );
    }

    return <ProfileContentLoader />;
  }, [state.profile]);

  const rendererEducation = useMemo(() => {
    if (state.init) {
      const { Positions: education } = state.profile;

      const actions = {
        onEditEducation: handleEditEducation,
      };

      if (Permission.isAllowedDeleteCandidatePosition(user.permissions)) {
        actions.onDeleteEducation = handleDeletePosition;
      }

      return (
        <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
          <BasicSection
            title="Education"
            buttons={[
              {
                caption: 'Add Education',
                handler: handleAddEducation,
              },
            ]}
          >
            {education && education.length > 0 && education.map(position => (
              <ProfileItemEducation
                data={position}
                actions={actions}
                key={position.PositionID}
              />
            ))}
          </BasicSection>
        </PanelContainer>
      );
    }
  }, [handleAddEducation, handleDeletePosition, handleEditEducation, state.init, state.profile, user.permissions]);

  const rendererAcademics = useMemo(() => {
    if (state.init) {
      const {
        Confidential: {
          UndergraduateMajor,
          GmatName,
          GpaName,
          MathSatName,
          ScoreName,
          VerbalSatName,
          WritingSatName,
        },
      } = state.profile;

      return (
        <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
          <BasicSection
            title="Academics"
            controls={[
              {
                caption: 'Edit',
                handler: handleEditAcademics,
              },
            ]}
          >
            <div
              className="candidate-profile-editor-page__text"
              style={{ margin: '15px 0' }}
            >
              <DataList
                objectList={{
                  'Undergraduate Major': UndergraduateMajor,
                  GPA: GpaName,
                  'SAT (M/V/W)': `${MathSatName}/${VerbalSatName}/${WritingSatName}`,
                  'ACT Score': ScoreName,
                  GMAT: GmatName,
                }}
              />
            </div>
          </BasicSection>
        </PanelContainer>
      );
    }
  }, [state.profile]);

  const rendererSummary = useMemo(() => {
    if (state.init) {
      const { Summary } = state.profile;
      return (
        <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
          <BasicSection
            title="Summary"
            controls={[
              {
                caption: 'Edit',
                handler: handleEditSummary,
              },
            ]}
          >
            <div
              className="candidate-profile-editor-page__text"
              style={{ margin: '15px 0' }}
            >
              {Summary && (
                <ReadMore text={Summary} readMoreCharacterLimit={350} />
              )}
            </div>
          </BasicSection>
        </PanelContainer>
      );
    }
  }, [state.profile]);

  const rendererPreferences = useMemo(() => {
    if (state.init) {
      const {
        Preferences: { buyside, location, fundsize },
      } = state.profile;

      return (
        <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
          <BasicSection
            title="Preferences"
            controls={[
              {
                caption: 'Edit',
                handler: handleEditPreferences,
              },
            ]}
          >
            <DataList
              objectList={{
                'Investment Strategy Interests': buyside,
                Location: location,
                'Fund Size': fundsize,
              }}
            />
          </BasicSection>
        </PanelContainer>
      );
    }
  }, [state.profile]);

  const rendererPersonalInformation = useMemo(() => {
    if (state.init) {
      const {
        profile: {
          ManagePersonalAccount,
          ImmediateStartOpportunities,
          Languages,
          Citizenship,
        },
      } = state;

      return (
        <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
          <BasicSection
            title="Personal Information"
            controls={[
              {
                caption: 'Edit',
                handler: handleEditPersonalInfo,
              },
            ]}
          >
            <DataList
              objectList={{
                'Manage Account': ManagePersonalAccount,
                'Immediate Start': ImmediateStartOpportunities,
                'Foreign Languages': Object.values(Languages).map(
                  language => `${language.LanguageName} (${language.LevelName})`
                ),
                Citizenship: Citizenship,
              }}
            />
          </BasicSection>
        </PanelContainer>
      );
    }
  }, [state.profile]);

  const rendererReferences = useMemo(() => {
    if (state.init) {
      const { References } = state.profile;
      return (
        <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
          <BasicSection
            title="References"
            buttons={[
              {
                caption: 'Add Reference',
                handler: handleAddReference,
              },
            ]}
          >
            {References && References.length > 0 && (
              <div className="candidate-profile-editor-page__reference-list">
                {References.map(record => (
                  <ProfileItemReference
                    data={record}
                    action={{}}
                    key={uniqueId()}
                  />
                ))}
              </div>
            )}
          </BasicSection>
        </PanelContainer>
      );
    }
  }, [state.profile]);

  return (
    <PageLayout
      pageClass="candidate-profile-editor-page"
      pageTitle="Edit Profile"
      headerContent={rendererHeader}
      mainContent={
        <CandidateProfile>
          <CandidateExperience user={user} candidateId={id} hasEdit={true} />
          {rendererSummary}
          {rendererEducation}
          {rendererAcademics}
          {rendererPreferences}
          {rendererPersonalInformation}
          {rendererReferences}
        </CandidateProfile>
      }
      sidebarContent={
        <Sidebar>
          {isRecruiterPremiumRecFirm && (
            <CandidateNotesWidget candidateId={id} isEdit={true} />
          )}
          <CandidateInterviewDataWidget
            data={{ ...state.profile.InterviewData, id }}
            onClickButton={handleInterviewDataUpdate}
          />
          <UpdatedProfile id={id} ref={widgetRef} />
        </Sidebar>
      }
    />
  );
};

export default CandidateProfileEditor;
